import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api } from '../api/client';
import type { MeResponse, PublicUser, Papel } from '../api/types';

interface AuthState {
  user: PublicUser | null;
  loading: boolean;
  /** Endereço que inicia o login na Steam (a API monta a URL do OpenID). */
  loginUrl: string;
  /** Sem banco não há conta: o site segue navegável, mas não dá para entrar. */
  dbAvailable: boolean;
  /**
   * A API respondeu, seja como visitante ou como logado.
   *
   * Distingue "o servidor disse que não há banco" de "não consegui falar com o
   * servidor" — os dois zeram o `dbAvailable`, e confundir um com o outro fazia
   * a parede de login desaparecer sempre que a API piscava.
   */
  apiOk: boolean;
  /** Entra no painel: Master, Admin ou Desenvolvedor. */
  isAdmin: boolean;
  /** O perfil de quem está logado. Visitante é 'client'. */
  papel: Papel;
  /** Perfil Desenvolvedor: vê o painel, a API recusa qualquer alteração. */
  somenteLeitura: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: PublicUser) => void;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setData(await api.me());
    } catch {
      // Visitante ou API fora do ar: o site continua navegável sem conta.
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const value = useMemo<AuthState>(
    () => ({
      user: data?.user ?? null,
      loading,
      loginUrl: data?.loginUrl ?? '/api/auth/steam',
      dbAvailable: data?.dbAvailable ?? false,
      apiOk: data !== null,
      isAdmin: ['master', 'admin', 'dev'].includes(data?.user?.role ?? 'client'),
      papel: data?.user?.role ?? 'client',
      somenteLeitura: data?.user?.role === 'dev',
      refresh,
      logout,
      setUser: (user) => setData((prev) => (prev ? { ...prev, user } : prev)),
    }),
    [data, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
